import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper,
  CircularProgress,
  alpha
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';

/**
 * DocumentViewer component for displaying different types of documents
 * 
 * @param {Object} props
 * @param {string|File|Object} props.document - The document URL, File object, or object with url/documentPath to display
 * @param {string} props.documentType - The type of document (optional, will attempt to determine from URL or File)
 * @param {boolean} props.showDownload - Whether to show download button (default: false)
 * @param {string} props.title - Optional title for the document
 * @param {number} props.height - Height of the viewer (default: 600px)
 * @param {Object} props.containerProps - Additional props for the container
 */
const DocumentViewer = ({ 
  document, 
  documentType, 
  showDownload = false, 
  title,
  height = 600,
  containerProps = {}
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [documentUrl, setDocumentUrl] = useState('');
  const [fileType, setFileType] = useState(documentType || '');
  const [useIframe, setUseIframe] = useState(false);

  const getFileTypeFromExtension = (filename) => {
    if (!filename) return 'application/octet-stream';
    const ext = filename.split('.').pop()?.toLowerCase();
    const typeMap = {
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'gif': 'image/gif',
      'pdf': 'application/pdf',
      'doc': 'application/msword',
      'docx': 'application/msword'
    };
    return typeMap[ext] || 'application/octet-stream';
  };

  useEffect(() => {
    if (!document) {
      setError('No document provided');
      setLoading(false);
      return;
    }

    let docToProcess = document;
    if (Array.isArray(document)) {
      docToProcess = document.find((item) => item != null) || document[0];
      if (!docToProcess) {
        setError('No valid document in array');
        setLoading(false);
        return;
      }
    }

    // Handle File object
    const isFileObject = docToProcess instanceof File || 
      (docToProcess?.name && typeof docToProcess.size === 'number');
    
    if (isFileObject) {
      try {
        const url = URL.createObjectURL(docToProcess);
        setDocumentUrl(url);
        const detectedType = docToProcess.type || getFileTypeFromExtension(docToProcess.name);
        setFileType(detectedType);
        setUseIframe(false);
        setLoading(false);
        return () => URL.revokeObjectURL(url);
      } catch (error) {
        setError('Failed to create preview URL for the document');
        setLoading(false);
        return;
      }
    } 
    // Handle URL string
    else if (typeof docToProcess === 'string') {
      setDocumentUrl(docToProcess);
      setFileType(documentType || getFileTypeFromExtension(docToProcess));
      setUseIframe(docToProcess.startsWith('blob:') || docToProcess.startsWith('http'));
      setLoading(false);
    }
    else if (docToProcess && typeof docToProcess === 'object' && (docToProcess.url || docToProcess.documentPath)) {
      const urlToUse = docToProcess.url || docToProcess.documentPath;
      setDocumentUrl(urlToUse);
      setFileType(docToProcess.type || documentType || getFileTypeFromExtension(urlToUse));
      setUseIframe(urlToUse.startsWith('blob:') || urlToUse.startsWith('http'));
      setLoading(false);
    } else {
      setError('Invalid document format');
      setLoading(false);
    }
  }, [document, documentType]);

  const renderDocumentContent = () => {
    if (loading) {
      return (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100%',
          backgroundColor: alpha('#f5f5f5', 0.5)
        }}>
          <CircularProgress size={40} thickness={4} sx={{ color: '#1a365d' }} />
          <Typography sx={{ mt: 2, color: '#666', fontWeight: 500 }}>Loading document...</Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100%',
          backgroundColor: alpha('#fff9f9', 0.8)
        }}>
          <ErrorOutlineIcon sx={{ fontSize: 48, mb: 2, color: '#d32f2f' }} />
          <Typography sx={{ color: '#d32f2f', fontWeight: 500 }}>{error}</Typography>
        </Box>
      );
    }

    if (!documentUrl) {
      return (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100%',
          backgroundColor: alpha('#f5f5f5', 0.5)
        }}>
          <DescriptionOutlinedIcon sx={{ fontSize: 48, mb: 2, color: '#9e9e9e' }} />
          <Typography sx={{ color: '#757575' }}>No document to display</Typography>
        </Box>
      );
    }

    // Display based on file type
    if (fileType.startsWith('image/')) {
      return (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          height: '100%', 
          overflow: 'auto',
          padding: 2,
          backgroundColor: alpha('#f5f5f5', 0.3)
        }}>
          <img 
            src={documentUrl} 
            alt={title || "Document"} 
            style={{ 
              maxWidth: '100%', 
              maxHeight: '100%', 
              objectFit: 'contain',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              borderRadius: '4px'
            }} 
            onLoad={() => setLoading(false)}
            onError={() => setError('Failed to load image')}
          />
        </Box>
      );
    } else if (fileType === 'application/pdf') {
      const isBlobUrl = documentUrl.startsWith('blob:');
      const pdfStyle = {
        border: 'none',
        borderRadius: '2px',
        boxShadow: 'inset 0 0 10px rgba(0,0,0,0.05)',
        minHeight: '500px'
      };
      
      return (
        <Box sx={{ height: '100%', p: 0.5, backgroundColor: '#fafafa', position: 'relative' }}>
          {isBlobUrl ? (
            <embed src={documentUrl} type="application/pdf" width="100%" height="100%" style={pdfStyle} onLoad={() => setLoading(false)} />
          ) : useIframe ? (
            <iframe
              src={documentUrl}
              title={title || "PDF Document"}
              width="100%"
              height="100%"
              style={pdfStyle}
              onLoad={() => setLoading(false)}
              onError={() => setError('Failed to load PDF. Please try downloading the file to view it.')}
            />
          ) : (
            <object data={documentUrl} type="application/pdf" width="100%" height="100%" style={pdfStyle} onLoad={() => setLoading(false)}>
              <iframe src={documentUrl} title={title || "PDF Document"} width="100%" height="100%" style={pdfStyle} onLoad={() => setLoading(false)} onError={() => setError('Failed to load PDF. Please try downloading the file to view it.')} />
            </object>
          )}
        </Box>
      );
    } else {
      return (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100%',
          backgroundColor: alpha('#f5f5f5', 0.5),
          padding: 3
        }}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 4, 
              textAlign: 'center',
              borderRadius: '8px',
              backgroundColor: 'white',
              border: '1px solid #e0e0e0',
              maxWidth: '80%'
            }}
          >
            <DescriptionOutlinedIcon sx={{ fontSize: 48, mb: 2, color: '#0052CC' }} />
            <Typography variant="body1" gutterBottom sx={{ fontWeight: 500, color: '#333' }}>
              This document type cannot be previewed directly.
            </Typography>
          </Paper>
        </Box>
      );
    }
  };

  return (
    <Paper
      elevation={3}
      sx={{
        width: '100%',
        height: height,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '8px',
        border: '1px solid #e0e0e0',
        backgroundColor: '#fff',
        ...containerProps.sx
      }}
      {...containerProps}
    >
      {title && (
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            px: 3,
            py: 1.5,
            borderBottom: '1px solid #e0e0e0',
            backgroundColor: '#f8f9fa',
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px',
          }}
        >
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 600, 
              color: '#1a365d',
              fontSize: '0.95rem',
              fontFamily: '"Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
            }}
          >
            {title}
          </Typography>
        </Box>
      )}
      <Box sx={{ 
        flexGrow: 1, 
        overflow: 'auto',
        backgroundColor: alpha('#f8f9fa', 0.4)
      }}>
        {renderDocumentContent()}
      </Box>
    </Paper>
  );
};

export default DocumentViewer; 